"use client";

import { forwardRef, useImperativeHandle, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  ImageIcon,
  Sparkles,
  Upload,
} from "lucide-react";

import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { storage } from "@/config/firebase";
import { ensureStorageAuthenticated } from "@/config/storageAuth";

type UploadTask = {
  file: File;
  type: string;
  field: string;
  displayName: string;
  dgr: string;
};

type UploadProgress = {
  field: string;
  displayName: string;
  progress: number;
  status: "pending" | "uploading" | "done" | "error";
  url?: string;
  error?: string;
};

const uploadImageToFirebase = (
  file: File,
  type: string,
  animalDgr: string,
  onProgress: (progress: number) => void,
): Promise<string> => {
  if (!file || !storage) return Promise.reject("No file or storage");
  const sanitizedName = file.name.replace(/[^a-zA-Z0-9.]/g, "_");
  const fileName = `${Date.now()}_${sanitizedName}`;
  const storagePath = `images/silaAnimals/${type}/${fileName}`;
  const storageRef = ref(storage, storagePath);
  const metadata = {
    contentType: file.type,
    customMetadata: { animalType: type, userId: animalDgr },
  };
  const uploadTask = uploadBytesResumable(storageRef, file, metadata);
  return new Promise((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress(progress);
      },
      (error) => reject(error),
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        } catch (e) {
          reject(e);
        }
      },
    );
  });
};

const SequentialUploadNotifier = forwardRef(
  (
    {
      animalImages,
      lineageImages,
    }: {
      animalImages: File[];
      lineageImages: Array<{
        file?: File | string | undefined;
        type: string;
        field: string;
        displayName: string;
        dgr: string;
      }>;
    },
    ref,
  ) => {
    const [progressList, setProgressList] = useState<UploadProgress[]>([]);
    const [isVisible, setIsVisible] = useState(false);
    const [overallProgress, setOverallProgress] = useState(0);

    useImperativeHandle(ref, () => ({
      startUploads: async () => {
        // Build upload tasks from animalImages and lineageImages
        const animalTasks: UploadTask[] = animalImages.map((file, idx) => ({
          file,
          type: "main-animal",
          field: `animalImage_${idx}`,
          displayName: `Animal Image ${idx + 1}`,
          dgr: lineageImages[0]?.dgr || "", // or pass dgr as a prop if needed
        }));
        const lineageTasks: UploadTask[] = lineageImages
          .filter((item) => item.file instanceof File)
          .map((item) => ({
            file: item.file as File,
            type: item.type,
            field: item.field,
            displayName: item.displayName,
            dgr: item.dgr,
          }));
        const allTasks = [...animalTasks, ...lineageTasks];
        if (allTasks.length === 0) {
          return { animalImageUrls: [], lineageImageUrls: {} };
        }

        await ensureStorageAuthenticated();
        setIsVisible(true);
        setOverallProgress(0);

        let list: UploadProgress[] = allTasks.map((task) => ({
          field: task.field,
          displayName: task.displayName,
          progress: 0,
          status: "pending",
        }));
        setProgressList(list);
        let results: Record<string, string> = {};

        for (let i = 0; i < allTasks.length; i++) {
          const { file, type, field, dgr } = allTasks[i];
          list = list.map((item, idx) =>
            idx === i
              ? {
                  ...item,
                  status: "uploading",
                }
              : item,
          );
          setProgressList([...list]);
          try {
            const url = await uploadImageToFirebase(file, type, dgr, (p) => {
              list = list.map((item, idx) =>
                idx === i
                  ? {
                      ...item,
                      progress: p,
                    }
                  : item,
              );
              setProgressList([...list]);
              setOverallProgress(
                list.reduce((sum, u) => sum + (u.progress || 0), 0) /
                  allTasks.length,
              );
            });
            results[field] = url;
            list = list.map((item, idx) =>
              idx === i
                ? {
                    ...item,
                    progress: 100,
                    status: "done",
                    url,
                  }
                : item,
            );
            setProgressList([...list]);
          } catch (e: any) {
            list = list.map((item, idx) =>
              idx === i
                ? {
                    ...item,
                    status: "error",
                    error: e?.message || "Upload failed",
                  }
                : item,
            );
            setProgressList([...list]);
            throw e;
          }
        }
        setOverallProgress(100);

        // Return two arrays: animalImageUrls and lineageImageUrls (in order)
        const animalImageUrls = animalTasks
          .map((task) => results[task.field])
          .filter(Boolean);
        const lineageImageUrls = Object.fromEntries(
          lineageTasks.map((task) => [task.field, results[task.field]]),
        );

        return { animalImageUrls, lineageImageUrls };
      },
    }));

    const completedCount = progressList.filter(
      (u) => u.status === "done",
    ).length;
    const allCompleted =
      progressList.length > 0 && completedCount === progressList.length;
    const hasUploading = progressList.some((u) => u.status === "uploading");
    const pendingCount = progressList.filter(
      (u) => u.status === "pending",
    ).length;

    const handleClose = () => setIsVisible(false);

    return (
      <div className="z-50 ">
        <AnimatePresence>
          {isVisible && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
              }}
              className="fixed inset-4 sm:inset-auto sm:top-4 sm:right-4 sm:bottom-4 sm:w-96 lg:w-[32rem] z-50"
            >
              <div className="h-full flex flex-col backdrop-blur-2xl bg-white/90 border border-white/20 rounded-3xl shadow-2xl shadow-black/10 overflow-hidden">
                <div className="flex-shrink-0 p-6 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-b border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <motion.div
                        animate={
                          hasUploading
                            ? {
                                rotate: 360,
                                scale: [1, 1.1, 1],
                              }
                            : { rotate: 0 }
                        }
                        transition={{
                          rotate: {
                            duration: 2,
                            repeat: hasUploading ? Number.POSITIVE_INFINITY : 0,
                            ease: "linear",
                          },
                          scale: {
                            duration: 2,
                            repeat: hasUploading ? Number.POSITIVE_INFINITY : 0,
                          },
                        }}
                        className="relative"
                      >
                        {allCompleted ? (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 15,
                            }}
                            className="w-10 h-10 bg-gradient-to-r from-emerald-400 to-green-500 rounded-full flex items-center justify-center"
                          >
                            <CheckCircle2 className="w-6 h-6 text-white" />
                          </motion.div>
                        ) : (
                          <div className="w-10 h-10 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-full flex items-center justify-center relative">
                            <Upload className="w-5 h-5 text-white" />
                            {hasUploading && (
                              <motion.div
                                className="absolute -inset-1 border-2 border-indigo-300 rounded-full"
                                animate={{ rotate: 360 }}
                                transition={{
                                  duration: 3,
                                  repeat: Number.POSITIVE_INFINITY,
                                  ease: "linear",
                                }}
                              />
                            )}
                          </div>
                        )}
                      </motion.div>
                      <div>
                        <h3 className="font-bold text-lg text-slate-800">
                          {allCompleted
                            ? "Queue Complete!"
                            : "Processing Queue"}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <span>{completedCount} completed</span>
                          <span>•</span>
                          <span>{hasUploading ? "1 active" : "0 active"}</span>
                          <span>•</span>
                          <span>{pendingCount} pending</span>
                        </div>
                      </div>
                    </div>
                                        <button
                      onClick={handleClose}
                      className="h-9 w-9 p-0 bg-transparent hover:bg-white/20 rounded-full z-50 text-slate-800"
                    >
                      Close
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Overall Progress</span>
                      <div className="text-right">
                        <motion.span
                          className="font-semibold text-slate-800"
                          key={Math.round(overallProgress)}
                          initial={{ scale: 1.1 }}
                          animate={{ scale: 1 }}
                        >
                          {Math.round(overallProgress)}%
                        </motion.span>
                      </div>
                    </div>
                    <div className="w-full bg-slate-200/60 rounded-full h-2 overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full relative"
                        initial={{ width: 0 }}
                        animate={{ width: `${overallProgress}%` }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <div className="p-2">
                    <AnimatePresence>
                      {progressList.map((upload, index) => (
                        <motion.div
                          key={upload.field}
                          initial={{ opacity: 0, x: -20, scale: 0.95 }}
                          animate={{ opacity: 1, x: 0, scale: 1 }}
                          exit={{ opacity: 0, x: 20, scale: 0.95 }}
                          transition={{
                            delay: index * 0.05,
                            type: "spring",
                            stiffness: 300,
                            damping: 25,
                          }}
                          className={`mb-3 p-4 backdrop-blur-sm rounded-2xl border transition-all duration-300 ${
                            upload.status === "uploading"
                              ? "bg-indigo-50/80 border-indigo-200/50 shadow-md ring-2 ring-indigo-100"
                              : upload.status === "done"
                                ? "bg-emerald-50/80 border-emerald-200/50"
                                : upload.status === "error"
                                  ? "bg-red-50/80 border-red-200/50"
                                  : "bg-white/60 border-white/30 hover:bg-white/80"
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                  upload.status === "done"
                                    ? "bg-emerald-100 text-emerald-600"
                                    : upload.status === "uploading"
                                      ? "bg-indigo-100 text-indigo-600"
                                      : upload.status === "error"
                                        ? "bg-red-100 text-red-600"
                                        : "bg-slate-100 text-slate-500"
                                }`}
                              >
                                {index + 1}
                              </div>
                              <motion.div
                                initial={{ scale: 0, rotate: -180 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{
                                  delay: index * 0.05 + 0.2,
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 15,
                                }}
                                className="flex-shrink-0"
                              >
                                {upload.status === "done" ? (
                                  <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: [0, 1.3, 1] }}
                                    transition={{
                                      duration: 0.8,
                                      times: [0, 0.6, 1],
                                      type: "spring",
                                      stiffness: 300,
                                    }}
                                    className="w-8 h-8 bg-gradient-to-r from-emerald-400 to-green-500 rounded-full flex items-center justify-center"
                                  >
                                    <CheckCircle2 className="w-5 h-5 text-white" />
                                  </motion.div>
                                ) : upload.status === "error" ? (
                                  <div className="w-8 h-8 bg-gradient-to-r from-red-400 to-red-500 rounded-full flex items-center justify-center">
                                    <AlertCircle className="w-5 h-5 text-white" />
                                  </div>
                                ) : upload.status === "uploading" ? (
                                  <div className="relative w-8 h-8">
                                    <motion.div
                                      animate={{ rotate: 360 }}
                                      transition={{
                                        duration: 1.2,
                                        repeat: Number.POSITIVE_INFINITY,
                                        ease: "linear",
                                      }}
                                      className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-500 rounded-full"
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <ImageIcon className="w-3 h-3 text-indigo-500" />
                                    </div>
                                    <motion.div
                                      className="absolute -inset-1 border border-indigo-300/50 rounded-full"
                                      animate={{
                                        scale: [1, 1.2, 1],
                                        opacity: [0.5, 0, 0.5],
                                      }}
                                      transition={{
                                        duration: 2,
                                        repeat: Number.POSITIVE_INFINITY,
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                                    <Clock className="w-4 h-4 text-slate-400" />
                                  </div>
                                )}
                              </motion.div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="font-semibold text-slate-800 truncate text-sm">
                                  {upload.displayName}
                                </h4>
                                <motion.span
                                  className={`text-sm font-bold ml-2 ${
                                    upload.status === "uploading"
                                      ? "text-indigo-600"
                                      : upload.status === "error"
                                        ? "text-red-600"
                                        : "text-slate-700"
                                  }`}
                                  key={upload.progress}
                                  initial={{ scale: 1.2 }}
                                  animate={{ scale: 1 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  {Math.round(upload.progress)}%
                                </motion.span>
                              </div>
                              <div className="flex items-center justify-between text-xs mb-2">
                                <span
                                  className={
                                    upload.status === "uploading"
                                      ? "text-indigo-600"
                                      : upload.status === "error"
                                        ? "text-red-600"
                                        : "text-slate-500"
                                  }
                                >
                                  {upload.status === "done"
                                    ? "Upload complete"
                                    : upload.status === "uploading"
                                      ? "Uploading..."
                                      : upload.status === "error"
                                        ? upload.error || "Upload failed"
                                        : `Position ${index + 1} in queue`}
                                </span>
                              </div>
                              <div className="w-full bg-slate-200/60 rounded-full h-1.5 overflow-hidden">
                                <motion.div
                                  className={`h-full rounded-full relative ${
                                    upload.status === "done"
                                      ? "bg-gradient-to-r from-emerald-400 to-green-500"
                                      : upload.status === "error"
                                        ? "bg-gradient-to-r from-red-400 to-red-500"
                                        : "bg-gradient-to-r from-indigo-400 to-purple-500"
                                  }`}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${upload.progress}%` }}
                                  transition={{
                                    duration: 0.8,
                                    ease: [0.4, 0, 0.2, 1],
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
                <AnimatePresence>
                  {allCompleted && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      className="flex-shrink-0 p-6 bg-gradient-to-r from-emerald-50/80 to-green-50/80 border-t border-white/10"
                    >
                      <div className="text-center">
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{
                            delay: 0.2,
                            type: "spring",
                            stiffness: 300,
                          }}
                          className="inline-flex items-center gap-2 mb-2"
                        >
                          <Sparkles className="w-5 h-5 text-emerald-500" />
                          <span className="font-bold text-emerald-700">
                            Sequential Upload Complete!
                          </span>
                        </motion.div>
                        <p className="text-sm text-emerald-600">
                          Successfully uploaded all {progressList.length} images
                          in sequence
                        </p>
                        <p className="text-xs text-emerald-500 mt-1">
                          One image at a time for optimal reliability
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

export default SequentialUploadNotifier;
